import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useEffect, useState } from "react";
import BreakdownExchangeRateColumn from "@/components/first-tier-supplier/breakdown-exchange-rate/BreakdownExchangeRateColumn";
import ViewRow from "@/components/first-tier-supplier/breakdown-exchange-rate/ViewRow";
import type {
  exchangeRateBody,
  exchangeRatePart,
  CurrencyOption,
  breakdownProcess,
} from "@/api/breakdownExchangeRateService";
import EditRow from "@/components/first-tier-supplier/breakdown-exchange-rate/EditRow";
import type { partSKP } from "@/api/supplierService";

interface BreakdownExchangeRateTableProps {
  rows: exchangeRatePart[];
  rowCount: number;
  isLoading: boolean;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  selectedParts: exchangeRatePart[];
  setSelectedParts: React.Dispatch<React.SetStateAction<exchangeRatePart[]>>;
  handleFlagPart: (partInfo: exchangeRatePart) => () => void;
  handleOpenHistory: (partId: number) => () => void;
  canEdit: boolean;
  currencyOptions: CurrencyOption[];
  isAdding: boolean;
  isEditing: boolean;
  newPart: exchangeRateBody;
  setNewPart: React.Dispatch<React.SetStateAction<exchangeRateBody>>;
  editedPart: exchangeRateBody[];
  setEditedPart: React.Dispatch<React.SetStateAction<exchangeRateBody[]>>;
  partOptions: partSKP[];
  validationErrors: { [key: number]: { [field: string]: string } };
  oemProcess: breakdownProcess[];
  spareProcess: breakdownProcess[];
}

const BreakdownExchangeRateTable = ({
  rows,
  rowCount,
  isLoading,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  selectedParts,
  setSelectedParts,
  handleFlagPart,
  handleOpenHistory,
  canEdit,
  currencyOptions,
  isAdding,
  isEditing,
  newPart,
  setNewPart,
  editedPart,
  setEditedPart,
  partOptions,
  validationErrors,
  oemProcess,
  spareProcess,
}: BreakdownExchangeRateTableProps) => {
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [isOpenOEMOtherProcess, setIsOpenOEMOtherProcess] =
    useState<boolean>(false);
  const [isOpenSpareOtherProcess, setIsOpenSpareOtherProcess] =
    useState<boolean>(false);

  useEffect(() => {
    if (
      rows.every((part) =>
        selectedParts.some((row) => row.part_id === part.part_id),
      ) &&
      rows.length > 0
    ) {
      setIsAllSelected(true);
    } else {
      setIsAllSelected(false);
    }
  }, [selectedParts, rows]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelect = (part: exchangeRatePart, checked: boolean) => {
    if (checked) {
      setSelectedParts((prev) => [...prev, part]);
    } else {
      setSelectedParts((prev) =>
        prev.filter((p) => p.part_id !== part.part_id),
      );
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedParts((prev) => [
        ...prev,
        ...rows.filter((part) => !prev.some((p) => p.part_id === part.part_id)),
      ]);
    } else {
      setSelectedParts([]);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
          position: "relative",
          width: "100%",
          backgroundColor: "#f3f3f3ff",
        }}
      >
        <Table stickyHeader sx={{ tableLayout: "fixed", width: "auto" }}>
          <TableHead>
            <TableRow>
              <BreakdownExchangeRateColumn
                isAllSelected={isAllSelected}
                handleSelectAll={handleSelectAll}
                oemProcess={oemProcess}
                spareProcess={spareProcess}
                isOpenOEMOtherProcess={isOpenOEMOtherProcess}
                setIsOpenOEMOtherProcess={setIsOpenOEMOtherProcess}
                isOpenSpareOtherProcess={isOpenSpareOtherProcess}
                setIsOpenSpareOtherProcess={setIsOpenSpareOtherProcess}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {isAdding && (
              <EditRow
                formData={newPart}
                setFormData={setNewPart}
                partDropdownOptions={partOptions}
                currencyOptions={currencyOptions}
                errors={validationErrors[0] || {}}
                isAdding={true}
                oemProcess={oemProcess}
                spareProcess={spareProcess}
                isOpenOEMOtherProcess={isOpenOEMOtherProcess}
                isOpenSpareOtherProcess={isOpenSpareOtherProcess}
              />
            )}
            {!isLoading &&
              rows.map((part) => {
                const editedPartData = editedPart.find(
                  (p) => p.part_id === part.part_id,
                );

                return (
                  <TableRow
                    key={part.part_id}
                    sx={{
                      backgroundColor: !part.part_price ? "#d3d3d3" : "white",
                    }}
                  >
                    {isEditing &&
                    selectedParts.some((p) => p.part_id === part.part_id) ? (
                      <EditRow
                        formData={
                          editedPartData || {
                            part_id: part.part_id,
                            exchange_rate_supplier_id:
                              part.exchange_rate_supplier_id,
                            currency_id: part.currency_id,
                            currency_code: part.currency_code,
                            price: part.price,
                            is_active: part.is_active,
                            oem_other_process: part.oem_other_process,
                            spare_other_process: part.spare_other_process,
                          }
                        }
                        setFormData={(updater) => {
                          setEditedPart((prev) => {
                            const existing = prev.find(
                              (p) => p.part_id === part.part_id,
                            );
                            if (existing) {
                              return prev.map((p) =>
                                p.part_id === part.part_id
                                  ? typeof updater === "function"
                                    ? updater(p)
                                    : updater
                                  : p,
                              );
                            } else {
                              const newData =
                                typeof updater === "function"
                                  ? updater({
                                      part_id: part.part_id,
                                      exchange_rate_supplier_id:
                                        part.exchange_rate_supplier_id,
                                      currency_id: part.currency_id,
                                      currency_code: part.currency_code,
                                      price: part.price,
                                      is_active: part.is_active,
                                      oem_other_process: part.oem_other_process,
                                      spare_other_process:
                                        part.spare_other_process,
                                    })
                                  : updater;
                              return [...prev, newData];
                            }
                          });
                        }}
                        partDropdownOptions={partOptions}
                        currencyOptions={currencyOptions}
                        errors={validationErrors[part.part_id] || {}}
                        isAdding={false}
                        part={{
                          partId: part.part_id,
                          partNumber: part.part_number,
                          partName: part.part_name,
                          ac: part.ac,
                          rt: part.rt,
                        }}
                        oemProcess={oemProcess}
                        spareProcess={spareProcess}
                        isOpenOEMOtherProcess={isOpenOEMOtherProcess}
                        isOpenSpareOtherProcess={isOpenSpareOtherProcess}
                      />
                    ) : (
                      <ViewRow
                        part={part}
                        selectedParts={selectedParts}
                        handleSelect={handleSelect}
                        handleFlagPart={handleFlagPart}
                        handleOpenHistory={handleOpenHistory}
                        canEdit={canEdit}
                        oemProcess={oemProcess}
                        spareProcess={spareProcess}
                        isOpenOEMOtherProcess={isOpenOEMOtherProcess}
                        isOpenSpareOtherProcess={isOpenSpareOtherProcess}
                      />
                    )}
                  </TableRow>
                );
              })}
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Skeleton variant="text" height={40} animation="wave" />
                </TableCell>
                <TableCell colSpan={6}>
                  <Skeleton variant="text" height={40} animation="wave" />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={12}
                  align="center"
                  sx={{ height: 150, verticalAlign: "middle", fontWeight: 500 }}
                >
                  ไม่มีข้อมูล
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={rowCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </Box>
  );
};

export default BreakdownExchangeRateTable;
